import { pick } from 'lodash-es';
import { cdnAssetOptionsKeys } from '../../types/cdn-asset-options';
import { cdnEntityOptionsKeys } from '../../types/cdn-entity-options';
import { VistaMediaType } from './types/media-type';
export class VistaCdnClient {
    /**
     * Creates a Vista CDN Client instance.
     * @param configuration The configuration settings of the Vista CDN client.
     */
    constructor(configuration) {
        /**
         * Media types not currently supported by the Vista CDN.
         * If this changes in future versions, this can be injected via config, or exposed via an API call.
         */
        this.unsupportedMediaTypes = [VistaMediaType.FilmTrailer, VistaMediaType.FilmShortTrailer];
        /**
         * Returns true if the media type is supported by the Vista CDN.
         * @param mediaType The media type to check
         */
        this.isMediaTypeSupported = (mediaType) => {
            return !this.unsupportedMediaTypes.find(m => m === mediaType);
        };
        this.configuration = configuration;
    }
    /**
     * URL builder for Film assets.
     * @param filmId The ID of the Film entity.
     * @param options Options for the Film entity.
     */
    film(filmId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(filmId, options);
        return {
            poster: urlBuilder(VistaMediaType.FilmPoster),
            backdrop: urlBuilder(VistaMediaType.FilmBackdrop),
            advertisement: urlBuilder(VistaMediaType.FilmAdvertisement),
            motionPoster: urlBuilder(VistaMediaType.FilmMotionPoster),
            still: urlBuilder(VistaMediaType.FilmStill),
            titleTreatment: urlBuilder(VistaMediaType.FilmTitleTreatment),
            trailer: urlBuilder(VistaMediaType.FilmTrailer),
            shortTrailer: urlBuilder(VistaMediaType.FilmShortTrailer)
        };
    }
    /**
     * URL builder for Site assets.
     * @param siteId The ID of the Site entity.
     * @param options Options for the Site entity.
     */
    site(siteId, options) {
        // todo: maybe this needs to be renamed to reflect that it's actually the
        // cinema operator code, not the site ID.
        const urlBuilder = this.getUrlBuilderForEntity(siteId, options);
        return {
            image: urlBuilder(VistaMediaType.SiteImage),
            gallery: urlBuilder(VistaMediaType.SiteGallery)
        };
    }
    /**
     * URL builder for Item assets.
     * @param itemId The ID of the Item entity.
     * @param options Options for the Item entity.
     */
    item(itemId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(itemId, options);
        return {
            image: urlBuilder(VistaMediaType.ItemImage),
            advertisement: urlBuilder(VistaMediaType.ItemAdvertisement)
        };
    }
    /**
     * URL builder for Ticket Type assets.
     * @param ticketTypeId The ID of the Ticket Type entity.
     * @param options Options for the Ticket Type entity.
     */
    ticketType(ticketTypeId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(ticketTypeId, options);
        return {
            image: urlBuilder(VistaMediaType.TicketTypeImage)
        };
    }
    /**
     * URL builder for Advertising Profile assets.
     * @param advertisingProfileId The ID of the Advertising Profile entity.
     * @param options Options for the Advertising Profile entity.
     */
    advertisingProfile(advertisingProfileId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(advertisingProfileId, options);
        return {
            image: urlBuilder(VistaMediaType.AdvertisingProfileImage),
            video: urlBuilder(VistaMediaType.AdvertisingProfileVideo)
        };
    }
    /**
     * URL builder for Promotion assets.
     * @param promotionId The ID of the Promotion entity.
     * @param options Options for the Promotion entity.
     */
    promotion(promotionId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(promotionId, options);
        return {
            image: urlBuilder(VistaMediaType.PromotionImage),
            advertisement: urlBuilder(VistaMediaType.PromotionAdvertisement)
        };
    }
    /**
     * URL builder for Attribute assets.
     * @param attributeId The ID of the Attribute entity.
     * @param options Options for the Attribute entity.
     */
    attribute(attributeId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(attributeId, options);
        return {
            icon: urlBuilder(VistaMediaType.AttributeIcon),
            advertisement: urlBuilder(VistaMediaType.AttributeAdvertisement)
        };
    }
    /**
     * URL builder for Event assets.
     * @param eventId The ID of the Event entity.
     * @param options Options for the Event entity.
     */
    event(eventId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(eventId, options);
        return {
            gallery: urlBuilder(VistaMediaType.EventGallery),
            poster: urlBuilder(VistaMediaType.EventPoster)
        };
    }
    /**
     * URL builder for Censor Rating assets.
     * @param censorRatingId The ID of the Censor Rating entity.
     * @param options Options for the Censor Rating entity.
     */
    censorRating(censorRatingId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(censorRatingId, options);
        return {
            icon: urlBuilder(VistaMediaType.CensorRatingIcon)
        };
    }
    /**
     * URL builder for Corporate Booking assets.
     * @param corporateBookingId The ID of the Corporate Booking entity.
     * @param options Options for the Corporate Booking entity.
     */
    corporateBooking(corporateBookingId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(corporateBookingId, options);
        return {
            image: urlBuilder(VistaMediaType.CorporateBookingImage)
        };
    }
    /**
     * URL builder for Loyalty Club assets.
     * @param loyaltyClubId The ID of the Loyalty Club entity.
     * @param options Options for the Loyalty Club entity.
     */
    loyaltyClub(loyaltyClubId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(loyaltyClubId, options);
        return {
            image: urlBuilder(VistaMediaType.LoyaltyClubImage),
            banner: urlBuilder(VistaMediaType.LoyaltyClubBanner),
            loginImage: urlBuilder(VistaMediaType.LoyaltyClubLoginImage),
            paymentImage: urlBuilder(VistaMediaType.LoyaltyClubPaymentImage),
            homeBanner: urlBuilder(VistaMediaType.LoyaltyClubHomeBanner),
            header: urlBuilder(VistaMediaType.LoyaltyClubHeader)
        };
    }
    /**
     * URL builder for Loyalty Membership Program assets.
     * @param loyaltyMembershipProgramId The ID of the Loyalty Membership Program entity.
     * @param options Options for the Loyalty Membership Program entity.
     */
    loyaltyMembershipProgram(loyaltyMembershipProgramId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(loyaltyMembershipProgramId, options);
        return {
            image: urlBuilder(VistaMediaType.LoyaltyMembershipProgramImage)
        };
    }
    /**
     * URL builder for Loyalty Reward Shop Item assets.
     * @param loyaltyRewardShopItemId The ID of the Loyalty Reward Shop Item entity.
     * @param options Options for the Loyalty Reward Shop Item entity.
     */
    loyaltyRewardShopItem(loyaltyRewardShopItemId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(loyaltyRewardShopItemId, options);
        return {
            image: urlBuilder(VistaMediaType.LoyaltyRewardShopItemImage)
        };
    }
    /**
     * URL builder for Loyalty Reward assets.
     * @param loyaltyRewardId The ID of the Loyalty Reward entity.
     * @param options Options for the Loyalty Reward entity.
     */
    loyaltyReward(loyaltyRewardId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(`${loyaltyRewardId}`, options);
        return {
            image: urlBuilder(VistaMediaType.LoyaltyRewardImage),
            thumbnail: urlBuilder(VistaMediaType.LoyaltyRewardThumbnail)
        };
    }
    /**
     * URL builder for Loyalty Subscription assets.
     * @param loyaltySubscriptionId The ID of the Loyalty Subscription entity.
     * @param options Options for the Loyalty Subscription's assets.
     */
    loyaltySubscription(loyaltySubscriptionId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(`${loyaltySubscriptionId}`, options);
        return {
            image: urlBuilder(VistaMediaType.LoyaltySubscriptionLogo)
        };
    }
    /**
     * URL builder for Web Payment Method assets.
     * @param webPaymentMethodId The ID of the Web Payment Method entity.
     * @param options Options for the Web Payment Method entity.
     */
    webPaymentMethod(webPaymentMethodId, options) {
        const urlBuilder = this.getUrlBuilderForEntity(`${webPaymentMethodId}`, options);
        return {
            icon: urlBuilder(VistaMediaType.WebPaymentMethodIcon)
        };
    }
    /**
     * Constructs and returns a URL for a CDN asset.
     * @param mediaType The CDN media type for the asset.
     * @param entityId The ID of the entity.
     * @param options Options for the asset.
     */
    getAssetUrl(mediaType, entityId, entityOptions, assetOptions) {
        if (!this.isMediaTypeSupported(mediaType)) {
            return undefined;
        }
        const url = `${this.configuration.url}/media/entity/get/${mediaType}/${entityId}`;
        const queryString = this.getQueryStringForOptions(entityOptions, assetOptions);
        return url + queryString;
    }
    /**
     * Creates a URL builder for a given entity.
     * @param entityId The ID of the entity.
     * @param options Options for the entity.
     */
    getUrlBuilderForEntity(entityId, entityOptions) {
        return (mediaType) => (assetOptions) => this.getAssetUrl(mediaType, entityId, entityOptions, assetOptions);
    }
    /**
     * Creates a query string for a CDN asset URL based on the supplied options.
     * @param options The options for the URL.
     */
    getQueryStringForOptions(entityOptions, assetOptions) {
        const queryParams = [];
        const { mediaApiToken } = this.configuration;
        const allAssetOptions = Object.assign(Object.assign(Object.assign({}, entityOptions), assetOptions), { mediaApiToken });
        const validAssetOptions = pick(allAssetOptions, [
            ...cdnEntityOptionsKeys,
            ...cdnAssetOptionsKeys,
            'mediaApiToken'
        ]);
        for (const optionName in validAssetOptions) {
            const value = validAssetOptions[optionName];
            if (value !== undefined && value !== null) {
                queryParams.push(`${[optionName]}=${value}`);
            }
        }
        return queryParams.length === 0 ? '' : `?${queryParams.join('&')}`;
    }
}
//# sourceMappingURL=vista-cdn-client.js.map